/**
 * When a `.chain` is worth creating for an app grant.
 *
 * A grant gives the app principal permission; the app only REACHES an external
 * folder through a `.chain` inside its own folder. So the Settings grant flow
 * auto-creates one — but a chain is pointless, and permanent clutter in the app
 * folder, when the folder is already reachable. Three cases:
 *
 *   1. the target IS the app folder (it is already the app's own root)
 *   2. the target lives INSIDE the app folder (a subfolder, e.g. `_appsystem`)
 *   3. a chain to that exact folder already exists
 *
 * NOT a case: an ancestor of the target is chained. A chain is a path, not
 * authority — one can exist with no grant behind it (it IS the app's access
 * request, see `reconcileGrants`; and "No Access" in Settings deliberately keeps
 * the chain and drops the grant). Treating that as reach would skip the chain the
 * app actually needs and leave it unable to read a folder the user just granted,
 * silently. The asymmetry decides it: a missing chain is invisible breakage, a
 * redundant one is visible clutter the user can delete.
 *
 * Pure so both the rule and its cases are unit-testable; the caller supplies the
 * target's ancestor ids (`filesApi.getBreadcrumbs`) and the app's chain targets.
 */
export interface ChainRedundancyInput {
  appFolderId: string
  targetFolderId: string
  /** Ancestor folder ids of the target, innermost or outermost order — both work.
   *  May include the target itself (breadcrumbs do); that is handled. */
  targetAncestorIds: readonly string[]
  /** `targetFileId` of every `.chain` currently in the app folder. */
  chainedTargetIds: readonly (string | null | undefined)[]
}

export interface ChainRedundancy {
  redundant: boolean
  /** Which rule fired — for the caller's log/copy, and to keep tests honest. */
  reason: 'app-folder' | 'inside-app' | 'already-chained' | null
}

export function chainRedundancy(input: ChainRedundancyInput): ChainRedundancy {
  const { appFolderId, targetFolderId, targetAncestorIds, chainedTargetIds } = input
  if (targetFolderId === appFolderId) return { redundant: true, reason: 'app-folder' }

  const ancestors = targetAncestorIds.filter(id => id && id !== targetFolderId)
  if (ancestors.includes(appFolderId)) return { redundant: true, reason: 'inside-app' }

  const chained = new Set(chainedTargetIds.filter((id): id is string => !!id))
  if (chained.has(targetFolderId)) return { redundant: true, reason: 'already-chained' }

  return { redundant: false, reason: null }
}
